import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import LoadingScreen from '../../Admin/Loading_Screen/LoadingScreen';
import API_BASE_URL from '../../ApiConfig/ApiConfig';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';

const RegisteredProduct = ({ closeForm }) => {
  const [productName, setProductName] = useState('');
  const [serialNo, setSerialNo] = useState('');
  const [modelNo, setModelNo] = useState('');
  const [warrantyPeriod, setWarrantyPeriod] = useState('0');
  const [expiryDate, setExpiryDate] = useState(null);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [customerId, setCustomerId] = useState('');
  const [email, setEmail] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [purchaseDate, setPurchaseDate] = useState('');
  const [isWarrantyEditable, setIsWarrantyEditable] = useState(false);
  const [productNames, setProductNames] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [productType, setProductType] = useState('');

  const navigate = useNavigate();
  
  useEffect(() => {
    const token = Cookies.get('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setCustomerId(decodedToken.customerId);
        setEmail(decodedToken.email);
      } catch (error) {
        console.error('Invalid token');
      }
    } else {
      console.error('No token found in local storage');
    }
  }, []);
  
  const token = Cookies.get("token");
  
  useEffect(() => {
    const fetchProductNamesAndDetails = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/registered-products/Products`, {
          headers: {
            "Content-Type": "application/json",
            "Authorization": token ? `Bearer ${token}` : "",
          }
        });
        if (response.status === 200) {
          const uniqueProducts = Array.from(
            new Map(
              response.data.map((item) => {
                const warrantyYears = parseInt(item.warrantyPeriod, 10) || 0;
                return [`${item.productName}-${item.modelNo}`, { ...item, warrantyPeriod: item.warrantyPeriod }];
              })
            ).values()
          );
          setProductNames(uniqueProducts);
        } else {
          console.error('Failed to fetch products');
        }
      } catch (error) {
        console.error('Error fetching product names and details:', error);
      }
    };

    fetchProductNamesAndDetails();
  }, []);

  useEffect(() => {
    if (purchaseDate && warrantyPeriod && warrantyPeriod !== 'MANUFACTURING DEFECTS') {
      const purchase = new Date(purchaseDate);
      const warranty = parseInt(warrantyPeriod, 10);

      const expiry = new Date(purchase);
      if (warrantyPeriod.includes('Months')) {
        expiry.setMonth(purchase.getMonth() + warranty);
      } else {
        expiry.setFullYear(purchase.getFullYear() + warranty);
      }

      setExpiryDate(expiry);
    }
  }, [purchaseDate, warrantyPeriod]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = productNames.filter((product) =>
        product.modelNo.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProducts(filtered.slice(0, 5));
      setShowDropdown(filtered.length > 0);
    } else {
      setFilteredProducts([]);
      setShowDropdown(false);
    }
  }, [searchTerm, productNames]);

  useEffect(() => {
    if (warrantyPeriod === 'MANUFACTURING DEFECTS') {
      setIsWarrantyEditable(true);
    } else {
      setIsWarrantyEditable(false);
    }
  }, [warrantyPeriod]);

  useEffect(() => {
    if (modelNo) {
      const matchedProduct = productNames.find((product) => product.modelNo === modelNo);
      if (matchedProduct) {
        setProductName(matchedProduct.productName);
        setWarrantyPeriod(matchedProduct.warrantyPeriod || '0');
        setIsWarrantyEditable(matchedProduct.warrantyPeriod === 'MANUFACTURING DEFECTS');
        setProductType(matchedProduct.productType || '');
      }
    }
  }, [modelNo, productNames]);

  const handleModelNoChange = (event) => {
    const inputModelNo = event.target.value;
    setModelNo(inputModelNo);
    setSearchTerm(inputModelNo);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    const formData = new FormData();
    formData.append('productName', productName);
    formData.append('productType', productType);
    formData.append('serialNo', serialNo);
    formData.append('modelNo', modelNo);
    formData.append('purchaseDate', purchaseDate);
    formData.append('warrantyPeriod', warrantyPeriod);
    formData.append('expiryDate', expiryDate ? expiryDate.toISOString().split('T')[0] : '');
    formData.append('customerId', customerId);
    formData.append('email', email);
    formData.append('invoice', invoiceFile);

    try {
      const response = await axios.post(`${API_BASE_URL}/Registered/RegisteredProduct`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          "Authorization": token ? `Bearer ${token}` : "",
        },
      });
      console.log('Warranty Extended:', response.data);
      setShowPopup(true);

      setTimeout(() => {
        navigate('/customer_dashboard');
      }, 1000);

    } catch (error) {
      console.error('Error extending warranty:', error);
      if (error.response) {
        alert(`Error: ${error.response.data.message || 'Something went wrong'}`);
      } else if (error.request) {
        alert('No response received from the server.');
      } else {
        alert(`Error: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (warrantyPeriod === 'MANUFACTURING DEFECTS' || isWarrantyEditable) {
      setIsWarrantyEditable(true);
    } else {
      setIsWarrantyEditable(false);
    }
  }, [warrantyPeriod]);

  const handleWarrantyPeriodChange = (event) => {
    const selectedPeriod = event.target.value;
    setWarrantyPeriod(selectedPeriod);
  };

  const handleInvoiceUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.size > 10 * 1024 * 1024) {
      alert('File size exceeds the limit of 10MB');
      return;
    }
    setInvoiceFile(file);
  };

  const handleProductSelection = (product) => {
    setProductName(product.productName);
    setModelNo(product.modelNo);
    setWarrantyPeriod(product.warrantyPeriod || '0');
    setIsWarrantyEditable(product.warrantyPeriod === 'MANUFACTURING DEFECTS');
    setSearchTerm(product.modelNo);
    setTimeout(() => {
      setShowDropdown(false);
    }, 100);
  };

  const handleClickOutside = (event, setShowPopup) => {
    if (event.target.classList.contains('popup-overlay')) {
      setShowPopup(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', (event) => handleClickOutside(event, setShowPopup));
    return () => {
      document.removeEventListener('mousedown', (event) => handleClickOutside(event, setShowPopup));
    };
  }, []);

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-white rounded-lg sm:rounded-xl max-w-4xl mt-8 sm:mt-12 lg:mt-20 mx-auto" style={{ boxShadow: '0px 10px 60px 0px #E2ECF980' }}>
      {loading && <LoadingScreen />}
      <h2 className="text-xl sm:text-2xl lg:text-2xl font-extrabold mb-6 sm:mb-8 text-center text-[#292D32]">Register Your Product</h2>
      <form onSubmit={handleSubmit}>
        {/* Row 1: Customer ID & Model Number */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <div>
            <label className="block text-gray-600 font-semibold mb-2 text-sm sm:text-base">Customer ID</label>
            <input
              type="text"
              value={customerId}
              readOnly
              className="w-full p-2 sm:p-3 border border-gray-300 rounded-md shadow-sm outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 appearance-none text-sm sm:text-base"
            />
          </div>
          <div className="relative">
            <label className="block text-gray-600 font-semibold mb-2 text-sm sm:text-base">Model Number</label>
            <input
              type="text"
              value={modelNo}
              onChange={handleModelNoChange}
              className="w-full p-2 sm:p-3 border border-gray-300 rounded-md shadow-sm outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 appearance-none mb-4 text-sm sm:text-base"
            />
            {showDropdown && (
              <div className="absolute w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-48 sm:max-h-60 overflow-y-auto z-20">
                {filteredProducts.map((product, index) => (
                  <div
                    key={index}
                    onClick={() => {
                      handleProductSelection(product);
                      setShowDropdown(false);
                    }}
                    className="px-3 sm:px-4 py-2 sm:py-3 cursor-pointer hover:bg-gray-200 text-sm sm:text-base"
                  >
                    {product.modelNo}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Row 2: Serial Number & Product Name */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mt-4">
          <div>
            <label className="block text-gray-600 font-semibold mb-2 text-sm sm:text-base">Serial Number</label>
            <input
              type="text"
              value={serialNo}
              onChange={(e) => setSerialNo(e.target.value)}
              className="w-full p-2 sm:p-3 border border-gray-300 rounded-md shadow-sm outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 appearance-none mb-4 text-sm sm:text-base"
              required
            />
          </div>
          <div>
            <label className="block text-gray-600 font-semibold mb-2 text-sm sm:text-base">Product Name</label>
            <input
              type="text"
              value={productName}
              readOnly
              className="w-full p-2 sm:p-3 border border-gray-300 rounded-md shadow-sm outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 appearance-none mb-4 text-sm sm:text-base"
            />
          </div>
        </div>

        {/* Row 3: Product Type & Purchase Date */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <div>
            <label className="block text-gray-600 font-semibold mb-2 text-sm sm:text-base">Product Type</label>
            <input
              type="text"
              value={productType}
              readOnly
              className="w-full p-2 sm:p-3 border border-gray-300 rounded-md shadow-sm outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 appearance-none mb-4 text-sm sm:text-base"
            />
          </div>
          <div>
            <label className="block text-gray-600 font-semibold mb-2 text-sm sm:text-base">Purchase Date</label>
            <input
              type="date"
              value={purchaseDate}
              onChange={(e) => setPurchaseDate(e.target.value)}
              className="w-full p-2 sm:p-3 border border-gray-300 rounded-md shadow-sm outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 appearance-none mb-4 text-sm sm:text-base"
            />
          </div>
        </div>

        {/* Row 4: Warranty Period & Expiry Date */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <div>
            <label className="block text-gray-600 font-semibold mb-2 text-sm sm:text-base">Warranty Period</label>
            {isWarrantyEditable ? (
              <select
                name="warrantyPeriod"
                value={warrantyPeriod}
                onChange={(e) => setWarrantyPeriod(e.target.value)}
                className="w-full p-2 sm:p-3 border border-gray-300 rounded-md shadow-sm outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 appearance-none text-sm sm:text-base"
              >
                <option value="">Select Warranty Period</option>
                <option value="6 Months">6 Months</option>
                <option value="1 Year">1 Year</option>
                <option value="2 Years">2 Years</option>
                <option value="3 Years">3 Years</option>
                <option value="4 Years">4 Years</option>
                <option value="5 Years">5 Years</option>
              </select>
            ) : (
              <input
                type="text"
                value={warrantyPeriod}
                readOnly
                className="w-full p-2 sm:p-3 border border-gray-300 rounded-md shadow-sm outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 appearance-none text-sm sm:text-base"
              />
            )}
          </div>
          <div>
            <label className="block text-gray-600 font-semibold mb-2 text-sm sm:text-base">Expiry Date</label>
            <input
              type="date"
              value={expiryDate ? expiryDate.toISOString().split('T')[0] : ''}
              readOnly
              className="w-full p-2 sm:p-3 border border-gray-300 rounded-md shadow-sm outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 appearance-none mb-4 text-sm sm:text-base"
            />
          </div>
        </div>

        {/* Row 5: Upload Invoice - Full Width */}
        <div className="mt-4">
          <label className="block text-gray-600 font-semibold mb-2 text-sm sm:text-base">Upload Invoice</label>
          <input
            type="file"
            onChange={handleInvoiceUpload}
            className="w-full p-2 sm:p-3 border border-gray-300 rounded-md shadow-sm outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 appearance-none mb-4 text-sm sm:text-base"
            required
          />
        </div>

        {/* Submit Button */}
        <div className="flex justify-end mt-4 sm:mt-6">
          <button
            type="submit"
            className="bg-[#1C50A3] w-full text-white py-2 sm:py-3 px-4 sm:px-6 rounded-lg shadow-lg transition duration-300 text-sm sm:text-base font-semibold"
          >
            Submit
          </button>
        </div>
      </form>

      {showPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-40 popup-overlay p-4">
          <div className="bg-white p-6 sm:p-8 rounded-lg shadow-xl max-w-sm sm:max-w-md w-full relative transform transition-all duration-300 ease-in-out">
            <p className="text-center text-base sm:text-lg text-gray-600">
              Your submission was successful. Thank you for your action!
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisteredProduct;
